import type { ReactNode } from 'react';
import type { BillingDocument } from '@traceio-org/platform-contracts';
import { EmptyPageState, NavigationGlyph, PageSection } from '@traceio-org/ui';
import { formatMoney } from '../../../billing/money';
import { AdminProblem } from '../../../components/AdminProblem';
import { LoadingBlock } from '../../../components/feedback';
import { DateTimeText, ScrollTable, StatusBadge } from '../../../components/records';
import { useAdmin } from '../../../context';
import { BILLING_PROVIDER_LABELS, formatPeriod } from '../../../format';
import { useAdminRead } from '../../../useAdminRead';

/** Only an https link to the provider's hosted invoice is offered; anything else is shown as no link. */
export function hostedInvoiceUrl(url: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
}

function DocumentsTable({ documents }: { documents: readonly BillingDocument[] }) {
  return (
    <ScrollTable caption="Invoices" className="admin-table admin-table--documents">
      <thead>
        <tr>
          <th scope="col">Invoice</th>
          <th scope="col">Provider</th>
          <th scope="col">Status</th>
          <th scope="col">Period</th>
          <th scope="col">Issued</th>
          <th scope="col" className="admin-num">
            Total
          </th>
          <th scope="col" className="admin-num">
            Due
          </th>
          <th scope="col" className="admin-num">
            Paid
          </th>
          <th scope="col">Link</th>
        </tr>
      </thead>
      <tbody>
        {documents.map((document) => {
          const url = hostedInvoiceUrl(document.hostedUrl);
          return (
            <tr key={document.id}>
              <th scope="row">
                <code>{document.providerDocumentId}</code>
              </th>
              <td>{BILLING_PROVIDER_LABELS[document.provider] ?? document.provider}</td>
              <td>
                <StatusBadge status={document.status} />
              </td>
              <td>
                {document.periodStart && document.periodEnd ? (
                  formatPeriod({ start: document.periodStart, end: document.periodEnd })
                ) : (
                  <span className="admin-muted">None</span>
                )}
              </td>
              <td>
                {document.issuedAt ? <DateTimeText iso={document.issuedAt} /> : <span className="admin-muted">Not yet</span>}
              </td>
              <td className="admin-num">{formatMoney(document.totalMicros, document.currency)}</td>
              <td className="admin-num">{formatMoney(document.amountDueMicros, document.currency)}</td>
              <td className="admin-num">{formatMoney(document.amountPaidMicros, document.currency)}</td>
              <td>
                {url ? (
                  <a href={url} target="_blank" rel="noopener noreferrer">
                    Open the invoice
                    <span className="admin-sr-only">, opens in a new tab</span>
                  </a>
                ) : (
                  <span className="admin-muted">No link</span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </ScrollTable>
  );
}

/** The provider's invoices for the organization, reconciled back by its webhooks and the periodic reconciliation. */
export function DocumentsSection({ organizationId }: { organizationId: string }) {
  const { api } = useAdmin();
  const key = `documents\u0000${organizationId}`;
  const read = useAdminRead(key, (signal) => api.listBillingDocuments(organizationId, signal));
  const documents = read.dataKey === key && !read.failure ? (read.data?.items ?? null) : null;

  let body: ReactNode;
  if (read.failure) {
    body = <AdminProblem failure={read.failure} subject="the invoices" onRetry={read.retry} />;
  } else if (!documents) {
    body = <LoadingBlock label="Loading the invoices" />;
  } else if (documents.length === 0) {
    body = (
      <EmptyPageState
        icon={<NavigationGlyph name="FileWarning" />}
        title="No invoice yet"
        description="Invoices arrive from the billing provider once a period is rated and reported."
      />
    );
  } else {
    body = <DocumentsTable documents={documents} />;
  }

  return (
    <PageSection
      title="Invoices"
      description="The billing provider's invoices, with their status and amounts as last reconciled. The link opens the provider's hosted invoice."
    >
      {body}
    </PageSection>
  );
}
